/**
 * RoleLayoutModal — 홈 편집 모드(관리자)에서 현재 위젯 배치를 부서(그룹) 기본 배치로 저장 (2026-11-02).
 *
 *   · 부서 여러 개를 골라 같은 배치를 한 번에 저장(tenants/{t}/units/{biz}/roles/{id}.home_layout).
 *   · "구성원 홈에 지금 적용" — 켜면 apply_version 을 올려 구성원이 다음에 홈을 열 때 현재 배치가 이 배치로 바뀐다.
 *     끄면 첫 진입(개인 배치 없음)·[기본 배치] 복원 때만 쓰인다.
 *   · 부서 설정에서 끈 위젯·권한 없는 위젯은 받는 쪽 홈에서 걸러지므로 여기서 따로 빼지 않는다.
 *   rules: roles write = isAdmin().
 */
import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { useToast } from "@/hooks/useToast";
import { useRoles } from "@/hooks/useRoles";
import { rolesRepo, type RoleUnit } from "@/services/roles.repository";

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
}

interface Props {
  unit: RoleUnit;
  layout: Record<string, unknown>[];
  onClose: () => void;
}

export function RoleLayoutModal({ unit, layout, onClose }: Props) {
  const toast = useToast();
  const { roles, loading } = useRoles(unit);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [apply, setApply] = useState(true);
  const [busy, setBusy] = useState(false);
  const dismiss = useBackdropDismiss(onClose, { disabled: busy });

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  const allOn = roles.length > 0 && roles.every((r) => picked.has(r.id));
  function toggleAll() {
    setPicked(allOn ? new Set() : new Set(roles.map((r) => r.id)));
  }

  async function save() {
    if (picked.size === 0) return toast.error("저장할 부서를 고르세요.");
    setBusy(true);
    try {
      const targets = roles.filter((r) => picked.has(r.id));
      await Promise.all(targets.map((r) => rolesRepo.saveHomeLayout(unit, r.id, layout, apply, r.homeLayout)));
      toast.success(
        `${targets.map((r) => r.label).join(", ")} 부서 기본 배치를 저장했습니다.${apply ? " 구성원이 다음에 홈을 열 때 적용됩니다." : ""}`,
      );
      onClose();
    } catch (e) {
      toast.error(`저장 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" {...dismiss}>
      <div className="modal-panel role-layout-modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>부서 기본 배치로 저장</h3>
          <button className="modal-close" onClick={onClose} aria-label="닫기" disabled={busy}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <p className="rl-help">
          지금 홈에 놓인 위젯 <b>{layout.length}개</b> 배치를 고른 부서의 기본 배치로 저장합니다.
          구성원 각자의 권한·부서 위젯 설정에 없는 위젯은 그 사람 홈에서 자동으로 빠집니다.
        </p>

        <div className="rl-list-hd">
          <span>부서</span>
          <button type="button" className="re-perm-all" onClick={toggleAll} disabled={loading || roles.length === 0}>
            {allOn ? "전체 해제" : "전체 선택"}
          </button>
        </div>
        <div className="rl-list">
          {loading ? (
            <div className="rl-empty">불러오는 중…</div>
          ) : roles.length === 0 ? (
            <div className="rl-empty">이 사업부에 부서가 없습니다.</div>
          ) : (
            roles.map((r) => (
              <label key={r.id} className="re-perm-item rl-item">
                <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} disabled={busy} />
                <span className="re-perm-label">{r.label}</span>
                <span className="re-perm-key">
                  {r.homeLayout
                    ? `${r.homeLayout.layout.length}개 · ${r.homeLayout.updatedAt ? ymd(r.homeLayout.updatedAt) : "저장됨"}`
                    : "시스템 기본"}
                </span>
              </label>
            ))
          )}
        </div>

        <label className={`re-allmenus rl-apply ${apply ? "is-on" : ""}`}>
          <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} disabled={busy} />
          <span className="re-allmenus-body">
            <span className="re-allmenus-title">구성원 홈에 지금 적용</span>
            <span className="re-allmenus-desc">
              켜면 구성원이 다음에 홈을 열 때 현재 배치가 이 배치로 바뀝니다(각자 바꾼 배치는 덮어씀).
              끄면 처음 들어오는 사람과 [기본 배치] 복원에만 쓰입니다.
            </span>
          </span>
        </label>

        <div className="modal-footer">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>취소</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={busy || picked.size === 0}>
            {busy ? "저장 중…" : `저장${picked.size ? ` (${picked.size})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
